<template>
  <label
    :class="[$style.config, $style.label, query && $style.hasValue]"
    data-search-field-v="3.3.1"
  >
    <input
      ref="search"
      v-model="query"
      type="search"
      :class="[$style.input, inputClass]"
      :placeholder="placeholder || localizedStrings['search']"
      @keyup.esc="onClearQuery"
      @keyup.enter="onRequestQuery"
    />
    <button
      v-show="query !== ''"
      :class="$style.clearTriggerWrap"
      @click="onClearQuery"
    >
      <IconTimes :class="$style.clearTrigger" />
    </button>
    <button :class="$style.searchTriggerButton" @click="onRequestQuery">
      <IconMagnifyingGlass :class="$style.searchTrigger" />
    </button>
  </label>
</template>

<script setup lang="ts">
const { localizedStrings } = useLocalizedStrings()

const emit = defineEmits(['request-query', 'clear-query'])

const props = defineProps<{
  placeholder?: string
  inputClass?: string
  initialQuery?: string | undefined
  focusOnMount?: boolean
}>()

const query = defineModel<string>({ default: '' })
const search = useTemplateRef('search')
const onRequestQuery = () => {
  emit('request-query', query?.value)
}

const onClearQuery = () => {
  query.value = ''
  emit('clear-query')
}

onMounted(() => {
  if (props.focusOnMount) {
    search?.value?.focus()
  }
  if (props.initialQuery) {
    query.value = props.initialQuery
  }
})
</script>

<style lang="scss" module>
.config {
  --bg-color: var(--gray-1);
  --border-color: var(--gray-5);
  --border-radius: 5px;
  --font-size: 18px;
  --font-style: italic;
  --padding: 20px;
  --placeholder-color: var(--gray-6);
  --text-color: var(--gray-8);
  --width: 205px;

  --search-icon-color: var(--purple-light);
  --search-icon-size: 18px;

  --clear-icon-color: var(--white);
  --clear-icon-size: 24px;
}

.label {
  align-items: center;
  background-color: var(--bg-color);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  display: flex;
  padding: 13px 18px 13px 20px;
  position: relative;
  width: var(--width);
}

.input {
  background-color: transparent;
  border: none;
  color: var(--text-color);
  flex: 1 1 auto;
  font-family: var(--roboto-condensed);
  font-size: var(--font-size);
  font-style: var(--font-style);
  height: 100%;
  line-height: 2;
  outline: none;
  padding: 0;
  width: 100%;

  &::placeholder {
    color: var(--placeholder-color);
  }
}

.clearTriggerWrap {
  flex: 0 0 var(--clear-icon-size);
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--clear-icon-size);
  width: var(--clear-icon-size);
  padding: 0;
  background: var(--purple-light);
  border: 0;
  border-radius: 50%;
  margin-right: 10px;
}

.clearTrigger {
  color: var(--clear-icon-color);
  cursor: pointer;
  max-width: calc(var(--clear-icon-size) - 8px);
  max-height: calc(var(--clear-icon-size) - 8px);
  width: 100%;
  height: auto;
}

.searchTriggerButton {
  padding: 0;
  border-color: transparent;
  background: none;
  height: var(--search-icon-size);
  width: var(--search-icon-size);
  color: var(--purple);
}

.searchTrigger {
  color: var(--search-icon-color);
  flex: 0 0 var(--search-icon-size);
  height: var(--search-icon-size);
  width: var(--search-icon-size);
}
</style>
